"use client";
import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icons } from "@/components/Icons";
import { Button, TopBar } from "@/components/ui";
import { chatApi, type ChatMessage } from "@/api/chat";

/**
 * Chat with the restaurant (R7).
 *
 * Transport is deliberately dull: poll while the screen is open, and let a
 * push notification cover the case where it is closed. An SSE stream would
 * be tidier on paper, but it would have to be relayed through the BFF from
 * the service that owns the conversation, and a customer cannot tell the
 * difference between a 5-second poll and a socket. That trade is worth
 * revisiting once chat is in real use, not before.
 *
 * Polling stops when the tab is hidden. A phone left on this screen in a
 * pocket should not spend the customer's battery and our request budget on
 * a conversation nobody is reading.
 */

const POLL_MS = 5000;
/** At most one keystroke heartbeat per this interval. The server's TTL is
 *  longer (8s), so the indicator stays lit between beats without the
 *  composer posting on every character. */
const TYPING_THROTTLE_MS = 3000;
const MAX_IMAGE_BYTES = 8 * 1024 * 1024;

type SendState = "idle" | "sending" | "error";

export function ChatScreen() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [draft, setDraft] = useState("");
  const [sendState, setSendState] = useState<SendState>("idle");
  const [attaching, setAttaching] = useState(false);
  // `null` = we have not been told yet, and say nothing. A guess here is
  // worse than silence: promising a quick reply at 2am costs more trust
  // than showing no banner at all.
  const [agentsOnline, setAgentsOnline] = useState<boolean | null>(null);
  const [restaurantTyping, setRestaurantTyping] = useState(false);
  const [readAt, setReadAt] = useState<number | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  // Set while the customer is scrolled up reading history: a poll landing
  // mid-read must not yank them back to the bottom.
  const pinnedToBottom = useRef(true);
  const lastTypingSent = useRef(0);

  const refresh = useCallback(async (opts?: { silent?: boolean }) => {
    try {
      const thread = await chatApi.thread();
      setMessages(thread.messages);
      setRestaurantTyping(Boolean(thread.restaurantTyping));
      setReadAt(thread.restaurantLastReadAt ? Date.parse(thread.restaurantLastReadAt) : null);
      setLoadFailed(false);
    } catch {
      // A failed poll is not worth an error state — the previous messages
      // are still valid and the next tick will very likely succeed. Only a
      // failed FIRST load is worth telling the customer about.
      if (!opts?.silent) setLoadFailed(true);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const refreshPresence = useCallback(async () => {
    try {
      const res = await chatApi.availability();
      setAgentsOnline(Boolean(res?.agentsOnline));
    } catch {
      // Not knowing whether anyone is there is a reason to say nothing, not
      // a reason to show "nobody is here".
      setAgentsOnline(null);
    }
  }, []);

  useEffect(() => {
    void refreshPresence();
  }, [refreshPresence]);

  useEffect(() => {
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer === null) timer = setInterval(() => void refresh({ silent: true }), POLL_MS);
    };
    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // Catch up immediately rather than waiting a full tick — coming back
        // to the app is exactly when a customer wants to see the reply.
        void refresh({ silent: true });
        // Someone could have gone home while the app was in the customer's
        // pocket.
        void refreshPresence();
        start();
      } else {
        stop();
      }
    };
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh, refreshPresence]);

  useEffect(() => {
    if (!pinnedToBottom.current) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    pinnedToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const sendText = async () => {
    const text = draft.trim();
    if (!text || sendState === "sending") return;
    setSendState("sending");
    try {
      await chatApi.send({
        type: "text",
        text,
        // Survives a retry after a dropped response, so the restaurant does
        // not answer the same sentence twice.
        clientMessageId: crypto.randomUUID(),
      });
      setDraft("");
      pinnedToBottom.current = true;
      await refresh({ silent: true });
      setSendState("idle");
    } catch {
      setSendState("error");
    }
  };

  const sendImage = async (file: File) => {
    if (file.size > MAX_IMAGE_BYTES) {
      setSendState("error");
      return;
    }
    setAttaching(true);
    try {
      const target = await chatApi.uploadTarget(file.type);
      await chatApi.upload(target, file);
      await chatApi.send({
        type: "image",
        gcsPath: target.gcsPath,
        mimeType: file.type,
        clientMessageId: crypto.randomUUID(),
      });
      pinnedToBottom.current = true;
      await refresh({ silent: true });
      setSendState("idle");
    } catch {
      setSendState("error");
    } finally {
      setAttaching(false);
    }
  };

  const sendLocation = () => {
    if (!navigator.geolocation) return;
    setAttaching(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await chatApi.send({
            type: "location",
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            clientMessageId: crypto.randomUUID(),
          });
          pinnedToBottom.current = true;
          await refresh({ silent: true });
          setSendState("idle");
        } catch {
          setSendState("error");
        } finally {
          setAttaching(false);
        }
      },
      () => {
        // A refused prompt is a choice, not a failure — say nothing and let
        // them type instead.
        setAttaching(false);
      },
      { enableHighAccuracy: true, timeout: 15_000 },
    );
  };

  return (
    <div style={shellStyle}>
      <TopBar title={t("v2.chat.title", "Chat with us")} onBack={() => navigate("/profile")} />

      {agentsOnline !== null && (
        <div style={presenceStyle} data-testid="chat-presence">
          <span style={{ ...dotStyle, background: agentsOnline ? "#16a34a" : "var(--ink-3)" }} />
          {agentsOnline
            ? t("v2.chat.online", "We're here — we usually reply in a few minutes")
            : t("v2.chat.offline", "Nobody's at the desk right now. Leave a message and we'll reply when we're back.")}
        </div>
      )}

      <div ref={scrollRef} onScroll={onScroll} style={listStyle} data-testid="chat-list">
        {loading ? (
          <p style={mutedText}>{t("v2.chat.loading", "Loading…")}</p>
        ) : loadFailed ? (
          <div style={centerBox}>
            <p style={mutedText}>
              {t("v2.chat.load_failed", "We couldn't load your messages. Pull down to try again.")}
            </p>
            <Button variant="outline" onClick={() => void refresh()}>
              {t("v2.chat.retry", "Try again")}
            </Button>
          </div>
        ) : messages.length === 0 ? (
          <div style={centerBox}>
            <Icons.info size={26} style={{ color: "var(--ink-3)" }} />
            <p style={mutedText}>
              {t("v2.chat.empty", "Ask us anything about your order — we usually reply in a few minutes.")}
            </p>
          </div>
        ) : (
          <>
            {messages.map((m) => (
              <Bubble key={m.id} message={m} readAt={readAt} />
            ))}
            {restaurantTyping && (
              <div style={{ display: "flex", justifyContent: "flex-start" }} data-testid="chat-typing">
                <div style={{ ...theirBubble, color: "var(--ink-2)", fontStyle: "italic" }}>
                  {t("v2.chat.typing", "Typing…")}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div style={composerStyle}>
        {sendState === "error" && (
          <p style={{ ...mutedText, color: "var(--danger-600, #c0392b)", marginBottom: 6 }}>
            {t("v2.chat.send_failed", "Couldn't send that. Try again.")}
          </p>
        )}
        <div style={composerRow}>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            aria-label={t("v2.chat.attach_photo", "Send a photo")}
            disabled={attaching}
            style={iconBtn}
          >
            <Icons.tag size={18} />
          </button>
          <button
            type="button"
            onClick={sendLocation}
            aria-label={t("v2.chat.share_location", "Share my location")}
            disabled={attaching}
            style={iconBtn}
          >
            <Icons.pin size={18} />
          </button>
          <input
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              const now = Date.now();
              if (e.target.value && now - lastTypingSent.current > TYPING_THROTTLE_MS) {
                lastTypingSent.current = now;
                void chatApi.typing();
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                void sendText();
              }
            }}
            placeholder={t("v2.chat.placeholder", "Type a message")}
            aria-label={t("v2.chat.placeholder", "Type a message")}
            style={inputStyle}
          />
          <button
            type="button"
            onClick={() => void sendText()}
            disabled={!draft.trim() || sendState === "sending"}
            aria-label={t("v2.chat.send", "Send")}
            style={{ ...iconBtn, color: "var(--primary-600)" }}
          >
            <Icons.check size={18} />
          </button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            // Reset first: picking the SAME file twice in a row fires no
            // change event otherwise, so a failed send could never be retried.
            e.target.value = "";
            if (file) void sendImage(file);
          }}
        />
      </div>
    </div>
  );
}

function Bubble({ message, readAt }: { message: ChatMessage; readAt: number | null }) {
  const { t } = useTranslation();
  const mine = message.author === "customer";
  // Only the customer's OWN messages carry a read mark, and only once an
  // agent opened the thread after the message landed.
  const seen = mine && readAt !== null && Date.parse(message.timestamp) <= readAt;
  return (
    <div style={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start", marginBottom: 8 }}>
      <div style={mine ? myBubble : theirBubble}>
        {message.type === "text" && (
          <span style={{ whiteSpace: "pre-wrap" }} dir="auto">{message.text}</span>
        )}
        {message.type === "image" && message.imageUrl && (
          <img
            src={message.imageUrl}
            alt={t("v2.chat.photo", "Photo")}
            style={{ maxWidth: 220, borderRadius: 10, display: "block" }}
          />
        )}
        {seen && (
          <span style={readMarkStyle} data-testid="chat-read">
            {t("v2.chat.read", "Read")}
          </span>
        )}
        {message.type === "location" && message.latitude != null && (
          <a
            href={`https://maps.google.com/?q=${message.latitude},${message.longitude}`}
            target="_blank"
            rel="noopener"
            style={{ color: "inherit", fontWeight: 700 }}
          >
            {t("v2.chat.location_shared", "Location shared")}
          </a>
        )}
      </div>
    </div>
  );
}

const shellStyle: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  height: "100%",
  minHeight: 0,
};

const presenceStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 7,
  padding: "8px 14px",
  fontSize: 12.5,
  color: "var(--ink-2)",
  background: "var(--surface-2)",
  borderBottom: "1px solid var(--border)",
};

const dotStyle: CSSProperties = {
  width: 7,
  height: 7,
  borderRadius: 999,
  flex: "0 0 auto",
};

const listStyle: CSSProperties = {
  flex: 1,
  overflowY: "auto",
  padding: "12px 14px",
  minHeight: 0,
};

const centerBox: CSSProperties = {
  height: "100%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: 10,
  textAlign: "center",
  padding: 24,
};

const composerStyle: CSSProperties = {
  borderTop: "1px solid var(--border)",
  background: "var(--surface-1)",
  padding: "10px 12px calc(env(safe-area-inset-bottom, 0px) + 10px)",
};

const composerRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
};

const inputStyle: CSSProperties = {
  flex: 1,
  minWidth: 0,
  height: 40,
  border: "1px solid var(--border)",
  borderRadius: 20,
  padding: "0 14px",
  fontSize: 14,
  fontFamily: "inherit",
  background: "var(--surface-2)",
  color: "var(--ink-1)",
  outline: "none",
};

const iconBtn: CSSProperties = {
  background: "transparent",
  border: 0,
  color: "var(--ink-2)",
  padding: 8,
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
  borderRadius: 999,
};

const bubbleBase: CSSProperties = {
  maxWidth: "78%",
  borderRadius: 16,
  padding: "8px 12px",
  fontSize: 14,
  lineHeight: 1.45,
};

const myBubble: CSSProperties = {
  ...bubbleBase,
  background: "var(--primary-600)",
  color: "#fff",
  borderBottomRightRadius: 4,
};

const theirBubble: CSSProperties = {
  ...bubbleBase,
  background: "var(--surface-2)",
  color: "var(--ink-1)",
  border: "1px solid var(--border)",
  borderBottomLeftRadius: 4,
};

const readMarkStyle: CSSProperties = {
  display: "block",
  marginTop: 3,
  fontSize: 10.5,
  opacity: 0.75,
  textAlign: "end",
};

const mutedText: CSSProperties = {
  fontSize: 13,
  color: "var(--ink-2)",
  margin: 0,
};
